// src/components/Home/Home.jsx
import React, { useCallback, useState, useMemo, useRef } from 'react';
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { FaFileAlt, FaLinkedin, FaEnvelope, FaPython, FaHtml5, FaReact, FaNodeJs, FaGitAlt, FaNetworkWired, FaRobot, FaMicrosoft } from 'react-icons/fa';
import { SiTypescript, SiAngular, SiJson, SiVercel, SiOpenai } from 'react-icons/si';
import Carousel from './Carousel';
import './home.css';
import sainteAnneLogo from '../assets/Ste_Anne_Collegial_logo_seul_RENV.png'
import bocconiLogo from '../assets/bocconi-university-logo-png_seeklogo-463777.png'
import hecLogo from '../assets/Logo_HEC_Montreal.svg.png'
import polytechniqueLogo from '../assets/AS_267462342316035@1440779399427_l.png'
import KPMGLogo from '../assets/KPMG_NoCP_White.png'
import botpressLogo from '../assets/botpresslogo.png'
import profileImage from '../assets/images/Marchildon_Frederic0503_Bio.jpg'
import cvFile from '../assets/CV-Frédéric_Marchildon-English.pdf'

// Move particles config outside component to prevent re-initialization
const particlesConfig = {
  fpsLimit: 120,
  interactivity: {
    events: {
      onHover: {
        enable: true,
        mode: ["grab", "repulse"],
      },
      onClick: {
        enable: false,
        mode: "repulse",
      },
      resize: true,
    },
    modes: {
      grab: {
        distance: 140,
        line_linked: {
          opacity: 0.3,
        },
      },
      repulse: {
        distance: 80,
        duration: 0.3,
        factor: 8,
        speed: 0.3,
        maxSpeed: 4,
        easing: "ease-out-quad"
      }
    },
  },
  particles: {
    color: {
      value: "#ffffff",
    },
    links: {
      color: "#ffffff",
      distance: 150,
      enable: true,
      opacity: 0.1,
      width: 1,
    },
    move: {
      direction: "none",
      enable: true,
      outModes: {
        default: "bounce",
      },
      random: true,
      speed: 0.3,
      straight: false,
    },
    number: {
      density: {
        enable: true,
        area: 800,
      },
      value: 60,
    },
    opacity: {
      value: 0.8,
      anim: {
        enable: true,
        speed: 0.3,
        opacity_min: 0.1,
        sync: false
      }
    },
    shape: {
      type: "circle",
    },
    size: {
      value: { min: 1, max: 4 },
    },
  },
  detectRetina: true,
};

function Home() {
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(false);
  const [showThoughts, setShowThoughts] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');
  const [aboutMeSlideDirection, setAboutMeSlideDirection] = useState('');
  const [initialAnimationsComplete, setInitialAnimationsComplete] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const particlesRef = useRef(null);
  const totalCards = 4;

  // Handle window resize to detect mobile
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mark initial animations as complete after they finish
  React.useEffect(() => {
    const aboutMeElement = document.querySelector('.about-me-content');
    
    const handleAnimationEnd = (event) => {
      if (event.animationName === 'slideInAboutMe') {
        setInitialAnimationsComplete(true);
        if (aboutMeElement) {
          aboutMeElement.removeEventListener('animationend', handleAnimationEnd);
        }
      }
    };
    
    if (aboutMeElement) {
      aboutMeElement.addEventListener('animationend', handleAnimationEnd);
    }
    
    // Fallback timer in case event doesn't fire
    const fallbackTimer = setTimeout(() => {
      setInitialAnimationsComplete(true);
    }, 5500);
    
    return () => {
      if (aboutMeElement) {
        aboutMeElement.removeEventListener('animationend', handleAnimationEnd);
      }
      clearTimeout(fallbackTimer);
    };
  }, []);

  const handleMainButtonClick = () => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    if (showThoughts) {
      // Going back to Section 1 (About Me) from Thoughts
      setShowThoughts(false);
      setShowCarousel(false);
      setSlideDirection('slide-down');
      setAboutMeSlideDirection('slide-down');
      setActiveCardIndex(0); // Reset carousel indicator
      setTimeout(() => {
        setSlideDirection('');
        setAboutMeSlideDirection('');
        setIsAnimating(false);
      }, 1000);
    } else if (showCarousel) {
      // In Section 2 (Carousel), going back to Section 1 (About Me)
      setAboutMeSlideDirection('slide-down');
      setSlideDirection('slide-down');
      
      setTimeout(() => {
        setShowCarousel(false);
        setActiveCardIndex(0); // Reset carousel indicator
        setAboutMeSlideDirection('');
        setSlideDirection('');
        setIsAnimating(false);
      }, 1000);
    } else {
      // In Section 1 (About Me), going to Section 2 (Carousel)
      setSlideDirection('slide-in');
      setAboutMeSlideDirection('slide-up');
      setShowCarousel(true);
      setTimeout(() => {
        setSlideDirection('');
        setAboutMeSlideDirection('');
        setIsAnimating(false);
      }, 1000);
    }
  };

  // Memoize the particles component to prevent re-renders
  const particlesComponent = useMemo(() => (
    <Particles
      id="tsparticles"
      ref={particlesRef}
      init={particlesInit}
      options={particlesConfig}
    />
  ), [particlesInit]);

  // Mobile content sections
  const renderMobileContent = () => (
    <div className="mobile-content-container">
      {/* About Me Section Mobile - First */}
      <div className="mobile-section mobile-about-section">
        <div className="mobile-about-container">
          <div className="profile-photo-container-mobile">
            <img src={profileImage} alt="Profile" className="profile-photo-circular-mobile" />
          </div>
          <div className="mobile-about-text">
            <p>My early academic journey was plagued with the question of sciences or business. I've always liked <i>things</i> – logical problems with clear inputs and outputs. However, I like people even more.</p>
            <p>That duality has become the foundation of my career. During my double degree in Software Engineering and Business Administration at Polytechnique Montréal and HEC Montréal, I realized there was this cozy professional spot for me between two worlds that don't always talk to each other as well as they should.</p>
            <p>I'm constantly looking for opportunities to make use of this duality – because honestly, it's where I have the most fun. Give me a technical product that needs explaining, a client who needs convincing, or a room full of stakeholders who need alignment, and I'm in my element.</p>
            <p>To top it off, I've been blessed to start my career at the very beginning of this new age of AI. It's a pretty exciting time to be someone who can both build with it and explain it.</p>
            <p>Shoot me a message, I'm told I'm pretty easy to talk to :)</p>
          </div>
          
          {/* Social Links in About Me - Mobile Only */}
          <div className="mobile-about-social-links">
            <a href={cvFile} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="CV">
              <FaFileAlt />
            </a>
            <a href="https://www.linkedin.com/in/fredericmarchildon" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
              <FaLinkedin />
            </a>
            <a href="mailto:frederic.marchildon@outlook.com" className="social-icon" aria-label="Email">
              <FaEnvelope />
            </a>
          </div>
          
          {/* Small Skill Cards Mobile */}
        </div>
      </div>

      {/* Education Section Mobile */}
      <div className="mobile-section">
        <h2 className="mobile-section-title">Education</h2>
        
        <div className="education-item">
          <div className="school-logo">
            <img src={polytechniqueLogo} alt="Polytechnique Logo" />
          </div>
          <div className="school-info">
            <h3>Polytechnique Montréal | Montréal, QC <span className="year">2024</span></h3>
            <p>Bachelor's Degree in Software Engineering</p>
          </div>
        </div>
        
        <div className="education-item">
          <div className="school-logo">
            <img src={hecLogo} alt="HEC Logo" />
          </div>
          <div className="school-info">
            <h3>École des Hautes Études Commerciales | Montréal, QC <span className="year">2024</span></h3>
            <p>Bachelor's Degree in Business Administration</p>
          </div>
        </div>
        
        <div className="education-item">
          <div className="school-logo">
            <img src={bocconiLogo} alt="Bocconi Logo" />
          </div>
          <div className="school-info">
            <h3>Università Commerciale Luigi Bocconi | Milan, Italy <span className="year">2023</span></h3>
            <p>Bachelor's Degree in Business Administration, semester abroad</p>
          </div>
        </div>
        
        <div className="education-item">
          <div className="school-logo">
            <img src={sainteAnneLogo} alt="Sainte-Anne Logo" className="st-anne-logo" />
          </div>
          <div className="school-info">
            <h3>Collégial International Sainte-Anne | Montréal, QC <span className="year">2019</span></h3>
            <p>DEC in Natural Sciences</p>
          </div>
        </div>
      </div>

      {/* Experience Section Mobile */}
      <div className="mobile-section experience-tile">
        <h2 className="mobile-section-title">Notable Experience</h2>
        
        <div className="experience-item">
          <div className="company-logo">
            <img src={botpressLogo} alt="Botpress Logo" />
          </div>
          <div className="company-info">
            <h3>Solutions Architect | Botpress <span className="year">2026-Present</span></h3>
            <p>Gimme some time, I just got here.</p>
          </div>
        </div>
        
        <div className="experience-item">
          <div className="company-logo">
            <img src={KPMGLogo} alt="KPMG Logo" />
          </div>
          <div className="company-info">
            <h3>Consultant | KPMG <span className="year">2021-2022 | 2025-2026</span></h3>
            <p><strong> Led Copilot enterprise enablement for a major Canadian Crown corporation, scaling adoption to 1,200+ users;</strong> designed and delivered champion and training programs; owned the data analysis for the final executive report.</p>
            <p><strong>Developed Microsoft Copilot AI agents and training documentation for enterprise stakeholder demonstrations,</strong> creating proof-of-concept solutions and technical guides to support team onboarding and client presentations.</p>
            <p><strong>Executed CRM business analysis for a leading pension fund manager,</strong> gathering requirements, identifying critical issues, proposing solutions and formulating evidence-based hypotheses for implementation success.</p>
          </div>
        </div>
        
      </div>

      {/* Skills Section Mobile */}
      <div className="mobile-section">
        <h2 className="mobile-section-title">Technical Skills</h2>
        
        <div className="skills-section">
          <h3 className="skills-category">Programming Languages</h3>
          <div className="skills-list">
            <div className="skill-item">
              <SiTypescript className="skill-icon" />
              <span>TypeScript</span>
            </div>
            <div className="skill-item">
              <FaPython className="skill-icon" />
              <span>Python</span>
            </div>
            <div className="skill-item">
              <SiJson className="skill-icon" />
              <span>JSON</span>
            </div>
            <div className="skill-item">
              <FaHtml5 className="skill-icon" />
              <span>HTML/CSS</span>
            </div>
          </div>
        </div>
        
        <div className="skills-section">
          <h3 className="skills-category">Frameworks & Libraries</h3>
          <div className="skills-list">
            <div className="skill-item">
              <SiAngular className="skill-icon" />
              <span>Angular</span>
            </div>
            <div className="skill-item">
              <FaReact className="skill-icon" />
              <span>React</span>
            </div>
            <div className="skill-item">
              <FaNodeJs className="skill-icon" />
              <span>Node.js</span>
            </div>
          </div>
        </div>
        
        <div className="skills-section">
          <h3 className="skills-category">Tools & Technologies</h3>
          <div className="skills-list">
            <div className="skill-item">
              <FaGitAlt className="skill-icon" />
              <span>Git</span>
            </div>
            <div className="skill-item">
              <FaNetworkWired className="skill-icon" />
              <span>REST APIs</span>
            </div>
            <div className="skill-item">
              <SiVercel className="skill-icon" />
              <span>Vercel</span>
            </div>
            <div className="skill-item">
              <FaRobot className="skill-icon" />
              <span>LLMs</span>
            </div>
            <div className="skill-item">
              <SiOpenai className="skill-icon" />
              <span>OpenAI API</span>
            </div>
            <div className="skill-item">
              <FaMicrosoft className="skill-icon" />
              <span>Copilot Studio</span>
            </div>
            <div className="skill-item">
              <FaMicrosoft className="skill-icon" />
              <span>Power Automate</span>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section Mobile */}
      <div className="mobile-section">
        <h2 className="mobile-section-title">Software Projects</h2>
        
        <div className="experience-item">
          <div className="company-info">
            <h3>EcoLens <span className="year">Polytechnique, 2024</span></h3>
            <p style={{ fontWeight: 'bold'}}>Life-cycle assessment tool to enable users to conduct comprehensive life cycle analyses of consumer goods.</p>
            <p>Designed intuitive user interfaces and interactive data visualization tools for enhanced user experience.</p>
            <p>Implemented advanced features, including AI-assisted product search, and exporting data into AI-assisted analysis documents.</p>
          </div>
        </div>
        
        <div className="experience-item">
          <div className="company-info">
            <h3>Colorimage <span className="year">Polytechnique, 2023</span></h3>
            <p style={{ fontWeight: 'bold'}}>A collaborative drawing webapp akin to Microsoft Paint.</p>
            <p>Implemented features using TypeScript and Angular, handling both frontend and backend development aspects.</p>
            <p>Integrated features allowing users to log in, collaborate in real time, chat, save their drawings, organize them into albums, and more, enhancing user interaction and experience.</p>
          </div>
        </div>
      </div>

      {/* Thoughts Section Mobile */}
      {/* <div className="mobile-section">
        <h2 className="mobile-section-title animated-title">Some Thoughts & Inspirations</h2>
        <div className="mobile-thoughts-text">
          <p>Coming soon...</p>
        </div>
      </div> */}

      {/* Social Links Mobile */}
      <div className="mobile-social-links">
        <a href={cvFile} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="CV">
          <FaFileAlt />
        </a>
        <a href="https://www.linkedin.com/in/fredericmarchildon" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
          <FaLinkedin />
        </a>
        <a href="mailto:frederic.marchildon@outlook.com" className="social-icon" aria-label="Email">
          <FaEnvelope />
        </a>
      </div>
    </div>
  );

  return (
    <div className="home-container">
      {particlesComponent}
      
      {/* Header Section */}
      <div className={`header-section ${!showCarousel && !showThoughts ? (!initialAnimationsComplete ? 'initial-load' : 'positioned') : 'slide-up'} ${aboutMeSlideDirection}`}>
        <h1 className="name-title">Frédéric Marchildon</h1>
        <p className="quote-text">I want to help solve the business + AI = ? equation.</p>
      </div>

      {/* Render mobile content if on mobile, otherwise show desktop sections */}
      {isMobile ? renderMobileContent() : null}
      
      {/* About Me Content - Section 1 (Initial View) */}
      <div className={`about-me-content ${!showCarousel && !showThoughts ? (!initialAnimationsComplete ? 'initial-load' : 'positioned') : (showCarousel ? 'slide-up' : 'slide-out')} ${aboutMeSlideDirection}`}>
        <div className="about-me-section">
          <div className="about-me-centered">
            <div className="about-me-text">
              <p>My early academic journey was plagued with the question of sciences or business. I've always liked <i>things</i> – logical problems with clear inputs and outputs. However, I like people even more.</p>
              <p>That duality has become the foundation of my career. During my double degree in Software Engineering and Business Administration at Polytechnique Montréal and HEC Montréal, I realized there was this cozy professional spot for me between two worlds that don't always talk to each other as well as they should.</p>
              <p>I'm constantly looking for opportunities to make use of this duality – because honestly, it's where I have the most fun. Give me a technical product that needs explaining, a client who needs convincing, or a room full of stakeholders who need alignment, and I'm in my element.</p>
              <p>To top it off, I've been blessed to start my career at the very beginning of this new age of AI. It's a pretty exciting time to be someone who can both build with it and explain it.</p>
              <p>Shoot me a message, I'm told I'm pretty easy to talk to :)</p>
            </div>
          </div>
          <div className="about-me-right-section">
            <div className="profile-photo-container">
              <img src={profileImage} alt="Profile" className="profile-photo-circular" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Carousel - Section 2 (Conditional) */}
      {showCarousel && (
        <div className={`carousel-section ${slideDirection || 'slide-in'}`}>
          {/* Experience Title */}
          <div className="section-title-wrapper">
            <h2 className="section-title">Experience</h2>
            <p className="quote-text">The highlights so far.</p>
          </div>
          <div className="carousel-wrapper">
            <Carousel onActiveChange={setActiveCardIndex}>
          {/* Education Card */}
          <div className="content-section">
            <div className="tile education-tile">
              <h2 className="tile-title">Education</h2>
              
              <div className="education-item">
                <div className="school-logo">
                  <img src={polytechniqueLogo} alt="Polytechnique Logo" />
                </div>
                <div className="school-info">
            <h3>Polytechnique Montréal | Montréal, QC <span className="year">2024</span></h3>
                  <p>Bachelor's Degree in Software Engineering</p>
                </div>
              </div>
              
              <div className="education-item">
                <div className="school-logo">
                  <img src={hecLogo} alt="HEC Logo" />
                </div>
                <div className="school-info">
            <h3>École des Hautes Études Commerciales | Montréal, QC <span className="year">2024</span></h3>
                  <p>Bachelor's Degree in Business Administration</p>
                </div>
              </div>
              
              <div className="education-item">
                <div className="school-logo">
                  <img src={bocconiLogo} alt="Bocconi Logo" />
                </div>
                <div className="school-info">
            <h3>Università Commerciale Luigi Bocconi | Milan, Italy <span className="year">2023</span></h3>
                  <p>Bachelor's Degree in Business Administration, semester abroad</p>
                </div>
              </div>
              
              <div className="education-item">
                <div className="school-logo">
                  <img src={sainteAnneLogo} alt="Sainte-Anne Logo" className="st-anne-logo" />
                </div>
                <div className="school-info">
            <h3>Collégial international Sainte-Anne | Montréal, QC <span className="year">2019</span></h3>
                  <p>DEC in Natural Sciences</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Experience Card */}
          <div className="content-section">
            <div className="tile experience-tile">
              <h2 className="tile-title">Notable Experience</h2>
              
              <div className="experience-item">
                <div className="company-info">
                  <h3>Solutions Architect | Botpress <span className="year">2026-Present</span></h3>
                  <p>Gimme some time, I just got here.</p>
                </div>
                <div className="company-logo">
                  <img src={botpressLogo} alt="Botpress Logo" />
                </div>
              </div>
              
              <div className="experience-item">
                <div className="company-info">
                  <h3>Consultant | KPMG <span className="year">2021-2022 | 2025-2026</span></h3>
                  <p><strong> Led Copilot enterprise enablement for a major Canadian Crown corporation, scaling adoption to 1,200+ users;</strong> designed and delivered champion and training programs; owned the data analysis for the final executive report.</p>
                  <p><strong>Developed Microsoft Copilot AI agents and training documentation for enterprise stakeholder demonstrations,</strong> creating proof-of-concept solutions and technical guides to support team onboarding and client presentations.</p>
                  <p><strong>Executed CRM business analysis for a leading pension fund manager,</strong> gathering requirements, identifying critical issues, proposing solutions and formulating evidence-based hypotheses for implementation success.</p>
                </div>
                <div className="company-logo">
                  <img src={KPMGLogo} alt="KPMG Logo" />
                </div>
              </div>
              
            </div>
          </div>
          
          {/* Skills Card */}
          <div className="content-section">
            <div className="tile">
              <h2 className="tile-title">Technical Skills</h2>
              
              <div className="skills-section">
                <h3 className="skills-category">Programming Languages</h3>
                <div className="skills-list">
                  <div className="skill-item">
                    <SiTypescript className="skill-icon" />
                    <span>TypeScript</span>
                  </div>
                  <div className="skill-item">
                    <FaPython className="skill-icon" />
                    <span>Python</span>
                  </div>
                  <div className="skill-item">
                    <SiJson className="skill-icon" />
                    <span>JSON</span>
                  </div>
                  <div className="skill-item">
                    <FaHtml5 className="skill-icon" />
                    <span>HTML/CSS</span>
                  </div>
                </div>
              </div>
              
              <div className="skills-section">
                <h3 className="skills-category">Frameworks & Libraries</h3>
                                <div className="skills-list">
                  <div className="skill-item">
                    <SiAngular className="skill-icon" />
                    <span>Angular</span>
                  </div>
                  <div className="skill-item">
                    <FaReact className="skill-icon" />
                    <span>React</span>
                  </div>
                  <div className="skill-item">
                    <FaNodeJs className="skill-icon" />
                    <span>Node.js</span>
                  </div>
                </div>
              </div>
              
              <div className="skills-section">
                <h3 className="skills-category">Tools & Technologies</h3>
                <div className="skills-list">
                  <div className="skill-item">
                    <FaGitAlt className="skill-icon" />
                    <span>Git</span>
                  </div>
                  <div className="skill-item">
                    <FaNetworkWired className="skill-icon" />
                    <span>REST APIs</span>
                  </div>
                  <div className="skill-item">
                    <SiVercel className="skill-icon" />
                    <span>Vercel</span>
                  </div>
                  <div className="skill-item">
                    <FaRobot className="skill-icon" />
                    <span>LLMs</span>
                  </div>
                  <div className="skill-item">
                    <SiOpenai className="skill-icon" />
                    <span>OpenAI API</span>
                  </div>
                  <div className="skill-item">
                    <FaMicrosoft className="skill-icon" />
                    <span>Copilot Studio</span>
                  </div>
                  <div className="skill-item">
                    <FaMicrosoft className="skill-icon" />
                    <span>Power Automate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Projects Card */}
          <div className="content-section">
            <div className="tile projects-tile">
              <h2 className="tile-title">Software Projects</h2>
              
              <div className="experience-item">
       
                <div className="company-info">
                  <h3>EcoLens <span className="year">Polytechnique, 2024</span></h3>
                  <p style={{ fontWeight: 'bold'}}>Life-cycle assessment tool to enable users to conduct comprehensive life cycle analyses of consumer
                  goods.</p>
                  <p>Designed intuitive user interfaces and interactive data visualization tools for enhanced user experience.</p>
                  <p>Implemented advanced features, including AI-assisted product search, and exporting data into AI-assisted
                  analysis documents. </p>
                </div>
              </div>
              
              <div className="experience-item">

                <div className="company-info">
                  <h3>Colorimage <span className="year">Polytechnique, 2023</span></h3>
                  <p style={{ fontWeight: 'bold'}}>A collaborative drawing webapp akin to Microsoft Paint.</p>
                  <p>Implemented features using TypeScript and Angular,
                  handling both frontend and backend development aspects.</p>
                  <p>Integrated features allowing users to log in, collaborate in real time, chat, save their drawings, organize them into albums, and more, enhancing user interaction and experience.</p>
                </div>
              </div>
            </div>
          </div>
            </Carousel>
          </div>
          
          <div className="carousel-content-wrapper">
            {/* Carousel indicators */}
            <div className={`carousel-indicators-wrapper ${showCarousel ? slideDirection : ''}`}>
              <div className="carousel-indicators">
                {Array.from({ length: totalCards }, (_, index) => (
                  <div
                    key={index}
                    className={`indicator-dot ${index === activeCardIndex ? 'active' : ''}`}
                    aria-label={`Card ${index + 1} indicator`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Social Links - Always visible, fixed position */}
      <div className="content-wrapper">
        <div className={`social-links ${!initialAnimationsComplete ? 'initial-load' : 'positioned'}`}>
          <a href={cvFile} target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="CV">
            <FaFileAlt />
          </a>
          <a href="https://www.linkedin.com/in/fredericmarchildon" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
            <FaLinkedin />
          </a>
          <a href="mailto:frederic.marchildon@outlook.com" className="social-icon" aria-label="Email">
            <FaEnvelope />
          </a>
        </div>
      </div>

      {/* Thoughts Content */}
      {/* <div className={`thoughts-content ${showThoughts ? 'slide-in' : ''} ${!showCarousel && !showThoughts ? 'slide-out' : ''}`}>
        <div className="thoughts-section">
          <div className="section-title-wrapper">
            <h2 className="section-title">Some Thoughts & Inspirations</h2>
            <p className="quote-text">Ideas and insights that shape my perspective.</p>
          </div>
          <div className="thoughts-placeholder">
            <p>Coming soon...</p>
          </div>
        </div>
      </div> */}

      {/* Main Navigation Button */}
      <button 
        className={`main-nav-button ${showCarousel ? 'in-carousel' : ''} ${showThoughts ? 'in-thoughts' : ''} ${isAnimating ? 'disabled' : ''}`}
        onClick={handleMainButtonClick}
        disabled={isAnimating}
        aria-label={showThoughts ? "About me" : (showCarousel ? "About me" : "Experience")}
      >
        {showThoughts ? "About me" : (showCarousel ? "About me" : "Experience")}
      </button>
    </div>
  );
}

export default Home;